import { ShieldCheckIcon } from "assets/icons/ShieldCheck";
import { orderTypeConstants, payerTypeConstants } from "constants/order";
import useMatchingOrders from "hooks/useMatchingOrders";
import { usePayerInformation } from "hooks/usePayerInformation";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IFacilityRules, INonFacilityRules } from "types/Order/Order";

import { StepCard } from "../StepCard";
import { StepKey } from "../StepCard/helper";
import { useStepStatus } from "../StepCard/useStepStatus";
import { PrimaryInsuranceSection } from "./components/PrimaryInsuranceSection";
import { SecondaryInsuranceSection } from "./components/SecondaryInsuranceSection";
import { BENEFITS_REPORT_ACTION_ID } from "./helpers";

export function PatientBenefitsSection({
  redo = false,
  draft = false,
  copy = false,
  account,
  costApplicationRules,
  secondaryCostApplicationRules,
}: {
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  account: IAccount;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  secondaryCostApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
}) {
  const {
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();
  const query = useQuery();
  const accountId = query.get("accountId") || "";
  const [showSecondaryInsuranceButton, setShowSecondaryInsuranceButton] =
    useState(true);
  const { selectedDraftOrder: draftOrder } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToRedo: orderToRedo } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToCopy: orderToCopy } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { payersNameInfo, payersCodesInfo } = useSelector(
    (state: any) => state.orderSlice
  );

  const { data: accountData } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });

  const orderTypeField = watch("orderType");
  // Deductible-reduction suggestion from the patient's earlier estimates. Runs off name + DOB +
  // visit date alone, so the banner shows before any CPT or benefit is entered (CLE-2769).
  useMatchingOrders();
  const status = useStepStatus(StepKey.BENEFITS);
  const secondaryInsuranceActivatedField = watch("secondaryInsuranceActivated");
  const payerField = watch("payer");
  const { loadingPayersName } = usePayerInformation(accountId);

  const mainProvider = useMemo(() => {
    return (
      accountData?.data?.providers.find(
        (aMainProvider) => !!aMainProvider.mainFacility
      ) || {}
    );
  }, [accountData]);

  const handleAddSecondaryInsurance = () => {
    setValue("secondaryInsuranceActivated", true);
  };

  const handleRemoveSecondaryInsurance = () => {
    setValue("secondaryInsuranceActivated", false);
    setValue("payerName2", null);
    setValue("payer2", null);
    setValue("memberId2", null);
    setValue("practiceType2", null);
    setValue("deductible2", null);
    setValue("outOfPocketMax2", null);
    setValue("flatCopay2", null);
    setValue("coinsurancePercent2", null);
    setValue("benefitDetails2", null);
    setValue("secondaryBenefitsUpdatedAt", null);
  };

  useEffect(() => {
    const payer = payersNameInfo.find(
      (payer: any) => payer.value?.payerId === payerField?.payerId
    );
    if (payer?.name === "Self-Pay Rate") {
      setShowSecondaryInsuranceButton(false);
      handleRemoveSecondaryInsurance();
    } else {
      setShowSecondaryInsuranceButton(true);
    }
  }, [payerField]);

  return (
    <StepCard
      status={status}
      hidden={orderTypeField !== orderTypeConstants.GFE}
      bodyClassName="pb-3 flex flex-column gap-2"
      icon={<ShieldCheckIcon width={20} height={20} />}
      title="Insurance & Benefits"
      description="Verify coverage before building the estimate"
      action={<div id={BENEFITS_REPORT_ACTION_ID} />}
    >
      <PrimaryInsuranceSection
        loadingPayersName={loadingPayersName}
        payersNameInfo={payersNameInfo}
        payersCodesInfo={payersCodesInfo}
        redo={redo}
        draft={draft}
        copy={copy}
        isEligibilityEnabledForMainProvider={!!mainProvider?.eligibilityEnabled}
        accountId={accountId}
        mainProvider={mainProvider}
        costApplicationRules={costApplicationRules}
        account={account}
        draftOrder={draftOrder}
        orderToRedo={orderToRedo}
        orderToCopy={orderToCopy}
      />
      {!secondaryInsuranceActivatedField && showSecondaryInsuranceButton && (
        <Button
          label="Add Secondary Insurance"
          onClick={handleAddSecondaryInsurance}
          className="p-button-outlined mt-4 ml-4 w-auto align-self-start"
        />
      )}
      {secondaryInsuranceActivatedField && (
        <SecondaryInsuranceSection
          loadingPayersName={loadingPayersName}
          payersNameInfo={payersNameInfo}
          payersCodesInfo={payersCodesInfo}
          redo={redo}
          draft={draft}
          copy={copy}
          isEligibilityEnabledForMainProvider={
            !!mainProvider?.eligibilityEnabled
          }
          accountId={accountId}
          mainProvider={mainProvider}
          costApplicationRules={secondaryCostApplicationRules}
          account={account}
          draftOrder={draftOrder}
          orderToRedo={orderToRedo}
          orderToCopy={orderToCopy}
          handleRemoveSecondaryInsurance={handleRemoveSecondaryInsurance}
        />
      )}
    </StepCard>
  );
}
